import React, { useEffect, useState, useCallback } from "react";
import Autoplay from "embla-carousel-autoplay";
import { Skeleton } from "@mui/material";
import { ImageBaseUrl, QueryPath } from "src/lib/constans";
import { useNavigate } from "react-router-dom";
import { parseQueryParams } from "src/helpers/helperFunctions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import "src/assets/css/Carousel/MainBannerCarousel.css";

interface IProps {
  isLoading?: boolean;
  images?: { [key: string]: any }[];
}

let DELAY = 4000;

const MainBannerSections: React.FC<IProps> = ({ images, isLoading }) => {
  const navigate = useNavigate();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [completedIndicators, setCompletedIndicators] = useState<number[]>([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Embla Carousel with autoplay
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: false,
      align: "start",
      containScroll: "keepSnaps",
      dragFree: false,
      // skipSnaps: false,
    },
    [
      Autoplay({
        delay: DELAY,
        stopOnInteraction: false,
        stopOnMouseEnter: false,
      }),
    ]
  );

  // Update selected index and track completed slides
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const currentIndex = emblaApi.selectedScrollSnap();
    setSelectedIndex(currentIndex);

    // Mark previous slides as completed
    const totalSlides = images?.length || 0;
    const newCompletedSlides = [];
    for (let i = 0; i < currentIndex; i++) {
      newCompletedSlides.push(i);
    }
    setCompletedIndicators(newCompletedSlides);
    
    // Mark first load as complete after first selection
    if (isFirstLoad) {
      setIsFirstLoad(false);
    }
  }, [emblaApi, images?.length, isFirstLoad]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Trigger first indicator animation on component mount
  useEffect(() => {
    if (images && images.length > 0 && isFirstLoad) {
      // Animation will start automatically via CSS
      // After animation completes, mark as not first load
      const timer = setTimeout(() => {
        setIsFirstLoad(false);
      }, 4100); // 4000ms animation + 100ms delay
      return () => clearTimeout(timer);
    }
  }, [images, isFirstLoad]);

  // Navigation functions
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  // Handle click
  const handleClick = (
    promotionalTagId: string,
    promotionalTagName: string
  ) => {
    if (promotionalTagId) {
      let query = parseQueryParams({
        [QueryPath.promotionalTag]: promotionalTagId,
        [QueryPath.promotionalTagName]: promotionalTagName,
      });
      navigate(`/category?${query}`);
    }
  };


  const totalLength = images?.length;

  return (
    <div className="view-area-width relative py-3 2xl:py-4">
      <div className="w-full relative">
        <section className="main__embla w-full">
          <div className="main__embla__viewport" ref={emblaRef}>
            <div className="main__embla__container">
              {isLoading ? (
                <div className="main__embla__slide">
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    style={{
                      borderRadius: "10px",
                    }}
                    height="65vh"
                  />
                </div>
              ) : (
                images?.map((item, index) => (
                  <div className="main__embla__slide" key={index}>
                    <img
                      src={item?.image || ""}
                      alt={`banner ${index + 1}`}
                      id={`main-banner-image-${index + 1}`}
                      onClick={() =>
                        handleClick(
                          item?.promotionalTagId,
                          item?.promotionalTagName
                        )
                      }
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
        <button
          className="main__embla__button absolute top-1/2 -translate-y-1/2 left-[-0.7rem]"
          onClick={scrollPrev}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="font-size-14px" />
        </button>
        <button
          className="main__embla__button absolute top-1/2 -translate-y-1/2 right-[-0.7rem]"
          onClick={scrollNext}
        >
          <FontAwesomeIcon icon={faChevronRight} className="font-size-14px" />
        </button>
      </div>
      <div className="main__embla__dots mt-2">
        {Array.from({ length: totalLength ? totalLength + 1 : 0 }).map((item, index) => (
          <button
            key={index}
            className={`main__embla__dot overflow-hidden ${index === 0 && "hidden"}`}
            style={{
              width:
                (index) === (selectedIndex + 1)
                  ? `calc(80% / ${totalLength ? totalLength - 3 : 0})`
                  : `calc(60% / ${totalLength ? totalLength : 0})`,
            }}
            onClick={() => scrollTo(index)}
          >
            <div
              className={`h-full bg-[#00443F]`}
              style={{
                width: index === (selectedIndex + 1) ? '100%' : 
                       index < (selectedIndex + 1) ? '100%' : '0%',
                transition: index === (selectedIndex + 1) ? 'width 4000ms ease-in-out' : 'none',
              }}
            ></div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default MainBannerSections;
